import COSFileList from '@gp/components/COSFileList';
import { GRStatusTag } from '@gp/components/StatusTag';
import TextAreaMore from '@gp/components/TextAreaMore';
import GRActions from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm/GRActions';
import MilestoneHistories from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm/MilestoneHistories';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { Form } from 'tea-component';
// import FeeUpdatedHistoriesBubble from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm/FeeUpdatedHistoriesBubble';
import { StaticGatereviewDecisionsCodes, UPLOAD_FOLDER_NAME_MAP } from '@gp/constants/const';
import Financials from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm/Financials';
import '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneForm/index.scss';
import type {
  IFeeUpdatedHistory,
  IMilestone,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import { getHasGRData } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { formatMaterials2Files, isNullOrUndefined, mathRound } from '@gp/utils/common';
import type { IBaseProps } from '@home/types/common';
import { extractTextFromHtml } from '@home/utils/common';

// Milestone费用、费用变更历史 展示UI需要用到的信息
interface IMilestoneFeeDataInfo {
  // 是否展示费用
  milestoneFeeShow: boolean;
  // 是否展示费用变更历史
  milestoneFeeUpdatedHistoriesShow: boolean;
  // 费用（格式化的）
  milestoneFeeFixed: string;
  // 费用变更历史
  milestoneFeeUpdatedHistories: IFeeUpdatedHistory[];
}

interface IMilestonesFormProps extends IBaseProps {
  isExpand: boolean;
  detailInfoAuthorityData?: Record<string, boolean>;
  projectDetailData?: Record<string, any>;
  currentMilestoneDate?: string;
  separatedMilestones?: IMilestone[];
  milestone?: IMilestone;
}

const getHasGRDecisionStatusData = (grDecisionStatus?: string): boolean => {
  return !(
    isNullOrUndefined(grDecisionStatus) ||
    grDecisionStatus === '' ||
    grDecisionStatus === StaticGatereviewDecisionsCodes.Empty
  );
};

const getHasGRDecisionCommentData = (grDecisionComment?: string): boolean => {
  return !!grDecisionComment?.trim();
};

/**
 * 获取费用、费用变更历史展示相关的信息（支持几种费用的计算，主要是复用相同的取值，判定逻辑代码）
 * @param milestone Milestone
 * @param fields 字段名配置
 * @param fields.fee 费用字段名
 * @param fields.histories 费用变更历史字段名
 * @returns 是否展示费用、费用、是否展示费用变更历史、费用变更历史
 */
const calcFeeDataInfo = (
  // Milestone
  milestone?: IMilestone,
  fields?: {
    // 费用字段名
    fee?: keyof IMilestone;
    // 费用变更历史字段名
    histories?: keyof IMilestone;
  },
): IMilestoneFeeDataInfo => {
  const {
    // 费用的字段名
    fee: feeField,
    // 费用变更历史的字段名
    histories: historiesField,
  } = fields || {};
  // 费用
  const milestoneAmount = milestone?.[feeField as keyof IMilestone] || '';
  // 费用（如果是string，进行trim操作）
  const milestoneAmountTrim =
    typeof milestoneAmount !== 'string' ? milestoneAmount : milestoneAmount.trim();
  // 费用变更历史
  const milestoneFeeUpdatedHistories = (milestone?.[historiesField as keyof IMilestone] ||
    []) as IFeeUpdatedHistory[];
  // 是否展示费用
  const milestoneFeeShow = !!milestoneAmountTrim;
  // 费用（数值格式化，具体格式规范看getFixedTwoNum方法实现逻辑）
  const milestoneFeeFixed = !milestoneFeeShow ? '' : (mathRound(milestoneAmountTrim) as any);
  // 是否展示费用变更历史（费用变更历史有数据才展示）
  const milestoneFeeUpdatedHistoriesShow =
    Array.isArray(milestoneFeeUpdatedHistories) && milestoneFeeUpdatedHistories.length > 0;

  return {
    milestoneFeeShow,
    milestoneFeeUpdatedHistoriesShow,
    milestoneFeeFixed,
    milestoneFeeUpdatedHistories,
  };
};

const MilestoneForm: React.FC<IMilestonesFormProps> = (props) => {
  const {
    style,
    className,
    isExpand,
    detailInfoAuthorityData,
    projectDetailData,
    currentMilestoneDate,
    separatedMilestones,
    milestone,
  } = props;

  const {
    milestone_name: milestoneNameEditable = false,
    milestone_gr: milestoneGREditable = false,
    milestone_gr_decision: milestoneGrDecisionEditable = false,
    milestone_feedback: milestoneKeyFeedbackEditable = false,
    milestone_action: milestoneActionItemsEditable = false,
    milestone_next_milestone_scope: milestoneNextMilestoneScopeEditable = false,
    milestone_dev_cost: milestoneDevCostEditable = false,
    milestone_financial_key_mlestone: milestoneFinancialsEditable = false,
    milestone_material: milestoneMaterialsEditable = false,
  } = detailInfoAuthorityData || {};

  const { project_id: projectId, project } = projectDetailData || {};

  const {
    milestone_id: milestoneId,
    action_items: milestoneActionItems,
    key_milestones_financial: milestonesFinancials,
    key_milestones_financial_forecast: milestonesFinancialsForecast,
    gate_review_decision: milestoneGateReviewDecision,
    gate_review: milestoneGateReview,
    milestone_history: milestoneHistories,
    key_feedback: milestoneKeyFeedback,
    next_milestone_scope: milestoneNextMilestoneScope,
    material: milestoneMaterials,
  } = milestone || {};

  // 是否展开的判断逻辑
  const isMilestoneShowAllContent = false;

  const showMilestoneKeyFeedback = !!extractTextFromHtml(milestoneKeyFeedback)?.trim();
  const showMilestoneNextMilestoneScope = !!extractTextFromHtml(
    milestoneNextMilestoneScope,
  )?.trim();
  const showMilestoneMaterials = Array.isArray(milestoneMaterials) && milestoneMaterials.length > 0;
  const milestoneMaterialFiles = !showMilestoneMaterials
    ? []
    : formatMaterials2Files(milestoneMaterials);

  const hasGRData = useMemo<boolean>(() => getHasGRData(milestone), [milestone]);

  const hasGRDecisionStatusData = getHasGRDecisionStatusData(milestoneGateReview);
  const hasGRDecisionCommentData = getHasGRDecisionCommentData(milestoneGateReviewDecision);
  const hasGRDecisionData = hasGRDecisionStatusData || hasGRDecisionCommentData;

  /** *** dev_cost费用、dev_cost费用变更历史 *****/
  const milestoneDevCostFeeDataInfo = useMemo<IMilestoneFeeDataInfo>(
    () => calcFeeDataInfo(milestone, { fee: 'dev_cost', histories: 'dev_cost_history' }),
    [milestone],
  );
  const milestoneDevCostFeeShow = milestoneDevCostFeeDataInfo.milestoneFeeShow;
  const milestoneDevCostFeeFixed = milestoneDevCostFeeDataInfo.milestoneFeeFixed;
  /** *** dev_cost费用、dev_cost费用变更历史 *****/

  return (
    <Form style={style} className={classnames('dbg-milestone-form', className)} readonly>
      {/* 新增meeting  E*/}
      {/* Milestone Histories */}
      {milestoneNameEditable && (
        <MilestoneHistories
          // 仅用milestone_history数据时，milestone history默认展开
          visible={!hasGRData}
          isShowAllContent={isMilestoneShowAllContent}
          histories={milestoneHistories}
        />
      )}
      {hasGRData && (milestoneGREditable || milestoneGrDecisionEditable) && (
        <Form.Item label="Decision">
          <Form.Text className="dbg-milestone-form-text">
            {hasGRDecisionData || '-'}
            {hasGRDecisionData && (
              <>
                {hasGRDecisionStatusData && milestoneGREditable && (
                  <GRStatusTag
                    className="dbg-milestone-form-gr-status-tag"
                    status={milestoneGateReview}
                  />
                )}
                {hasGRDecisionCommentData && milestoneGrDecisionEditable && (
                  <TextAreaMore
                    className="gp-word-break-word gp-all-dom-line-height-24 gp-text-area-more float-btn-left gp-inital-list-style dbg-rich-text-editor-content dbg-history-info"
                    text={milestoneGateReviewDecision}
                    type="shadow"
                    isMustNoButton
                    isShowAllContent
                    isShadowLayer={false}
                    ellipsisLineClamp={2}
                    lineHeight={22}
                    shadowInitBoxShowH={44}
                  />
                )}
              </>
            )}
          </Form.Text>
        </Form.Item>
      )}
      {hasGRData && milestoneKeyFeedbackEditable && (
        <Form.Item label="Key Feedback">
          <Form.Text className="dbg-milestone-form-text">
            {showMilestoneKeyFeedback || '-'}
            {showMilestoneKeyFeedback && (
              <TextAreaMore
                className="gp-word-break-word gp-all-dom-line-height-24 gp-text-area-more float-btn-left gp-inital-list-style dbg-rich-text-editor-content dbg-history-info"
                text={milestoneKeyFeedback}
                type="shadow"
                isMustNoButton
                isShowAllContent
                isShadowLayer={false}
                ellipsisLineClamp={3}
                lineHeight={22}
                shadowInitBoxShowH={66}
              />
            )}
          </Form.Text>
        </Form.Item>
      )}
      {hasGRData && milestoneActionItemsEditable && (
        <GRActions isShowAllContent grActions={milestoneActionItems} />
      )}
      {hasGRData && (milestoneDevCostEditable || milestoneFinancialsEditable) && (
        <Financials
          milestoneDevCostEditable={milestoneDevCostEditable}
          milestoneDevCostFeeShow={milestoneDevCostFeeShow}
          milestoneDevCostFeeFixed={milestoneDevCostFeeFixed}
          milestoneFinancialsEditable={milestoneFinancialsEditable}
          milestoneDevCostFeeDataInfo={milestoneDevCostFeeDataInfo}
          currentMilestoneDate={currentMilestoneDate}
          separatedMilestones={separatedMilestones}
          milestonesFinancials={milestonesFinancials}
          milestonesFinancialsForecast={milestonesFinancialsForecast}
        />
      )}
      {hasGRData && milestoneNextMilestoneScopeEditable && (
        <Form.Item label="Focus of the Next Review">
          <Form.Text className="dbg-milestone-form-text">
            {showMilestoneNextMilestoneScope || '-'}
            {showMilestoneNextMilestoneScope && (
              <TextAreaMore
                className="gp-word-break-word gp-all-dom-line-height-24 gp-text-area-more float-btn-left gp-inital-list-style dbg-rich-text-editor-content dbg-history-info"
                text={milestoneNextMilestoneScope}
                type="shadow"
                isMustNoButton
                isShowAllContent
                isShadowLayer={false}
                ellipsisLineClamp={3}
                lineHeight={22}
                shadowInitBoxShowH={66}
                // getIsFold={(v, isInitState) => {
                //   isInitState || setRecordsIsOverflow(v, record, records, setRecords);
                // }}
              />
            )}
          </Form.Text>
        </Form.Item>
      )}
      {hasGRData && milestoneMaterialsEditable && (
        <Form.Item label="Documents">
          <Form.Text>
            {showMilestoneMaterials || '-'}
            {showMilestoneMaterials && (
              <COSFileList
                key={`${isExpand}`}
                domain="milestone_materials"
                logKeys={{
                  previewLogKey: 'DETAIL_MILESTONE_GR_DOCUMENT_PREVIEW_SUBMIT',
                  downloadLogKey: 'DETAIL_MILESTONE_GR_DOCUMENT_DOWNLOAD_SUBMIT',
                  shareLogKey: 'DETAIL_MILESTONE_GR_DOCUMENT_SHARE_SUBMIT',
                }}
                urlSearchParams={{
                  project_id: projectId || '',
                  project: project || '',
                  milestone_id: milestoneId || '',
                }}
                files={milestoneMaterialFiles}
                uploadFolderName={UPLOAD_FOLDER_NAME_MAP.milestoneReview}
              />
            )}
          </Form.Text>
        </Form.Item>
      )}
    </Form>
  );
};

export default MilestoneForm;
